import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';
import { AppState } from '.';
import { Account, AccountUser, User } from '../models';
import { AccountUserService } from '../services';
import { Operator, Query } from '../models/query';

export const ACCOUNT_USERS_FEATURE_KEY = 'accountUsers';

type AccountUserEntity = AccountUser;
const accountUsersAdapter = createEntityAdapter<AccountUserEntity>();

interface AccountUserState extends EntityState<AccountUserEntity> {
  loading: boolean;
  error?: string;
  loadingPrefix: { [key: string]: boolean };
}

const createInitialState = (): AccountUserState => (
  accountUsersAdapter.getInitialState({
    loading: false,
    loadingPrefix: {},
  })
);

export const doGetByUserIdAndAccountId = createAsyncThunk(
  'accountUser/getByUserIdAndAccountId',
  async (input: Pick<AccountUser, 'userId' | 'accountId'>) => {
    const criteria: Array<Query<AccountUser>> = [
      { field: 'userId', operator: Operator.EQ, value: input.userId },
      { field: 'accountId', operator: Operator.EQ, value: input.accountId },
      { field: 'archived', operator: Operator.EQ, value: false },
      { limit: 1 },
    ];
    const data = await AccountUserService.getAccountUsers(...criteria);
    return data;
  },
);

const accountUsersSlice = createSlice({
  name: ACCOUNT_USERS_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetByUserIdAndAccountId.pending, state => {
      state.loading = true;
      state.loadingPrefix[doGetByUserIdAndAccountId.typePrefix] = true;
    });
    builder.addCase(doGetByUserIdAndAccountId.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.data.length > 0) {
        accountUsersAdapter.upsertOne(state, action.payload.data.at(0)!!);
      }
      state.loadingPrefix[doGetByUserIdAndAccountId.typePrefix] = false;
    });
    builder.addCase(doGetByUserIdAndAccountId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.loadingPrefix[doGetByUserIdAndAccountId.typePrefix] = false;
    });
  },
});

const selectAccountUserFeature = (state: AppState) => state[ACCOUNT_USERS_FEATURE_KEY];
const { selectAll } = accountUsersAdapter.getSelectors(selectAccountUserFeature);

export const selectOneByUserIdAndAccountId = (userId: User['id'], accountId: Account['id']) => createSelector(
  selectAll,
  all => all.find(it => it.userId === userId && it.accountId === accountId),
);

export const selectCountByAccountId = (accountId: Account['id']) => createSelector(
  selectAll,
  all => all.filter(it => it.accountId === accountId).length,
);

export const findAccountUserByAccountId = (accountId: Account['id']) => createSelector(
  selectAll,
  all => all.find(it => it.accountId === accountId),
);

export default accountUsersSlice.reducer;
